import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';

const mockDailyData = [
  { day: 'Mon', usage: 120, average: 150 },
  { day: 'Tue', usage: 135, average: 150 },
  { day: 'Wed', usage: 180, average: 150 },
  { day: 'Thu', usage: 110, average: 150 },
  { day: 'Fri', usage: 90, average: 150 },
  { day: 'Sat', usage: 75, average: 150 },
  { day: 'Sun', usage: 140, average: 150 },
];

const mockMonthlyData = [
  { month: 'Jan', usage: 3200, average: 3500 },
  { month: 'Feb', usage: 3100, average: 3500 },
  { month: 'Mar', usage: 2800, average: 3500 },
  { month: 'Apr', usage: 3500, average: 3500 },
  { month: 'May', usage: 3700, average: 3500 },
  { month: 'Jun', usage: 3600, average: 3500 },
  { month: 'Jul', usage: 2900, average: 3500 },
  { month: 'Aug', usage: 2700, average: 3500 },
  { month: 'Sep', usage: 3300, average: 3500 },
  { month: 'Oct', usage: 3400, average: 3500 },
  { month: 'Nov', usage: 3200, average: 3500 },
  { month: 'Dec', usage: 3000, average: 3500 },
];

const Analytics = () => {
  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Water Analytics</h1>
      
      <Tabs defaultValue="usage" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="usage">Usage Trends</TabsTrigger>
          <TabsTrigger value="comparisons">Comparisons</TabsTrigger>
          <TabsTrigger value="predictions">Predictions</TabsTrigger>
        </TabsList>
        
        <TabsContent value="usage" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Daily Water Usage</CardTitle>
              <CardDescription>Your water consumption over the past week</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={mockDailyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="usage" fill="#3bb4e0" name="Your Usage (Liters)" />
                    <Bar dataKey="average" fill="#34d399" name="City Average (Liters)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Monthly Water Usage</CardTitle>
              <CardDescription>Your water consumption over the past year</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mockMonthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="usage" stroke="#3bb4e0" name="Your Usage (Liters)" />
                    <Line type="monotone" dataKey="average" stroke="#34d399" name="City Average (Liters)" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="comparisons" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Neighborhood Comparison</CardTitle>
              <CardDescription>How your usage compares to your neighborhood</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { category: 'You', usage: 120 },
                    { category: 'Neighbors', usage: 150 },
                    { category: 'City', usage: 170 },
                    { category: 'Top 10%', usage: 80 }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="usage" fill="#3bb4e0" name="Daily Avg Usage (Liters)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="predictions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>AI Predictions</CardTitle>
              <CardDescription>Your projected water usage for the next month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[
                    { day: '1', actual: null, predicted: 125 },
                    { day: '2', actual: null, predicted: 130 },
                    { day: '3', actual: null, predicted: 128 },
                    { day: '4', actual: null, predicted: 115 },
                    { day: '5', actual: null, predicted: 120 },
                    { day: '6', actual: null, predicted: 118 },
                    { day: '7', actual: null, predicted: 122 },
                    { day: '8', actual: null, predicted: 127 },
                    { day: '9', actual: null, predicted: 125 },
                    { day: '10', actual: null, predicted: 115 },
                    { day: '11', actual: null, predicted: 110 },
                    { day: '12', actual: null, predicted: 105 },
                    { day: '13', actual: null, predicted: 100 },
                    { day: '14', actual: null, predicted: 95 },
                    { day: '15', actual: 120, predicted: null },
                    { day: '16', actual: 115, predicted: null },
                    { day: '17', actual: 118, predicted: null },
                    { day: '18', actual: 125, predicted: null },
                    { day: '19', actual: 130, predicted: null },
                    { day: '20', actual: 135, predicted: null },
                    { day: '21', actual: 125, predicted: null },
                    { day: '22', actual: 120, predicted: null },
                    { day: '23', actual: 115, predicted: null },
                    { day: '24', actual: 110, predicted: null },
                    { day: '25', actual: 105, predicted: null },
                    { day: '26', actual: 100, predicted: null },
                    { day: '27', actual: 110, predicted: null },
                    { day: '28', actual: 115, predicted: null },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="actual" stroke="#3bb4e0" name="Actual Usage (Liters)" />
                    <Line type="monotone" dataKey="predicted" stroke="#fbbf24" strokeDasharray="5 5" name="Predicted Usage (Liters)" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analytics;
